import { DbRef, orderedMedicalTitleRanking, InstructionType, ProcedureDocumentType, ProcedureStatus, GenericStatusInfoTitle, GenericStatusIndicator, SampleStatus, FailureReason, SupplyInstructionOptions, SupplyCondition, YesNoOptionLabel, CancelationUserType, Role, OfficeType } from '@caresend/types';
import { getInstruction } from './instruction.js';
import { getPath } from './path.js';
import { hasStatus } from './status.js';
import { decryptData, encryptData } from '../environment/data/privateHelpers.js';
import { formatTimeZoneDateSimple, getTimeZoneLabel, militaryHourToTime } from '../formatters/date.js';
import { buildBulletSeparatedString } from '../formatters/string.js';
import { isNullish, nullishFilter } from '../typeValidation/nullish.js';
import { isOrderedMedicalTitle } from '../typeValidation/user.js';

/**
 * ```
 * `${DbRef.PROCEDURES}/${id}/${childPath}`
 * ```
 */

function getProcedurePath(id, childPath) {
  return getPath(`${DbRef.PROCEDURES}/${id}`, childPath);
}
/**
 * ```
 * `${DbRef.SUPPLY_INSTRUCTION_CONFIG_OPTIONS}/${procedureID}/${childPath}`
 * ```
 */

function getSupplyInstructionConfigOptionsPath(procedureID, childPath) {
  return getPath(`${DbRef.SUPPLY_INSTRUCTION_CONFIG_OPTIONS}/${procedureID}`, childPath);
}
/**
 * ```
 * `${DbRef.PROCEDURES}/${procedureID}/samples/${sampleID}/${childPath}`
 * ```
 */

function getProcedureSamplePath(procedureID, sampleID, childPath) {
  return getPath(`${DbRef.PROCEDURES}/${procedureID}/samples/${sampleID}`, childPath);
}
/**
 * Given a task, returns the minimum medical title required to complete the task.
 */

const getMinimumTitleByTask = task => {
  const taskTypeTitles = Object.values(task.titles);
  const minimumTitleIndex = Math.max(...taskTypeTitles.map(title => orderedMedicalTitleRanking.indexOf(title)));
  let title;

  if (minimumTitleIndex === -1) {
    [title] = task.titles;
  } else {
    title = orderedMedicalTitleRanking[minimumTitleIndex];
  }

  if (!title || !isOrderedMedicalTitle(title)) return;
  return title;
};
/**
 * Will determine the minimum title required to complete the task for a given Procedure or Draft<Procedure>.
 */

const getMinimumOrderedMedicalTitleByTaskID = (taskID, tasks) => {
  if (!taskID) return;
  const task = tasks[taskID];
  if (!task) return;
  return getMinimumTitleByTask(task);
};
/**
 * Given a list of procedures, will loop through, and find the minimum title
 * to complete all of them.
 * This function is being used in ui-components and cloud,
 * so we need to pass variables as an input as we cannot fetch firebase from utils
 */

const getMinimumTitleForProcedures = (procedures, tasks) => {
  let currentMinimumTitleIndexForProcedures = orderedMedicalTitleRanking.length - 1;
  let title = orderedMedicalTitleRanking[currentMinimumTitleIndexForProcedures];
  procedures.forEach(async ({
    taskID
  }) => {
    const minimumTitle = getMinimumOrderedMedicalTitleByTaskID(taskID, tasks);
    if (!minimumTitle) return;
    const minimumTitleIndex = orderedMedicalTitleRanking.indexOf(minimumTitle);
    if (minimumTitleIndex === -1) return;

    if (minimumTitleIndex < currentMinimumTitleIndexForProcedures) {
      currentMinimumTitleIndexForProcedures = minimumTitleIndex;
      title = minimumTitle;
    }
  });
  return title;
};
/**
 * The passed instructions will be returned in the format of
 * buildBulletSeparatedString()
 */

const formatInstructions = instructions => {
  const formatted = instructions.map(instruction => {
    var _a;

    const noneFound = `No ${instruction.type} instructions provided`;

    if (instruction.type === InstructionType.FASTING && instruction.fields) {
      if (!((_a = instruction.fields[0]) === null || _a === void 0 ? void 0 : _a.answer)) return noneFound;
      return instruction.fields[0].answer;
    }

    if (!instruction.instruction) return noneFound;
    return instruction.instruction;
  });
  return buildBulletSeparatedString(formatted);
};
/**
 * Add the passed file to the procedure's documents and return the updated documents
 * object
 */

const updateProcedureDocuments = (procedure, file, documentType, info) => {
  var _a, _b;

  const documents = (_b = (_a = procedure === null || procedure === void 0 ? void 0 : procedure.documents) === null || _a === void 0 ? void 0 : _a.filter(doc => doc.type !== documentType)) !== null && _b !== void 0 ? _b : [];
  if (!file) return documents;
  const newDocument = {
    id: file.id,
    file,
    type: documentType,
    info
  };
  documents.push(newDocument);
  return documents;
};
const getProcedureDocumentsByType = (procedure, documentType = ProcedureDocumentType.REQUISITION_FORM) => {
  const documents = procedure === null || procedure === void 0 ? void 0 : procedure.documents;
  if (!documents) return;
  return documents.filter(({
    type
  }) => type === documentType);
};
const getFormattedInstructions = (procedure, instructionType = InstructionType.NURSE) => {
  const instructions = procedure === null || procedure === void 0 ? void 0 : procedure.instructions;
  if (!instructions) return;
  const matchedInstructions = instructions.filter(({
    type
  }) => type === instructionType);
  return formatInstructions(matchedInstructions);
};
/**
 * Adds or overwrites `supplyInstructionsUpdated` property on supply instruction
 * config, with `updatedAt` now.
 */

const markSupplyInstructionConfigUpdateTime = (config,
/** userID or 'python' */
updatedBy) => Object.assign(Object.assign({}, config), {
  supplyInstructionsUpdated: {
    updatedAt: Date.now(),
    updatedBy
  }
});
/**
 * Renders all selected time window(s) on multiple lines for display on detail
 * pages.
 *
 * Examples:
 *
 * Time(s) selected:
 * ```
 * 3/20/2023 - between 6 am and 10 am PDT
 * 3/20/2023 - between 10 am and 2 pm PDT
 * ```
 *
 * No time selected:
 * `The patient will pick a date and time`
 */

const getProcedureDateInfoDetailed = procedure => {
  const {
    dateInfo
  } = procedure;
  if (isNullish(dateInfo)) return;
  const requested = dateInfo.map(info => {
    if (!info.dateInstruction || !info.timeInstruction) return;
    const date = formatTimeZoneDateSimple(info.dateInstruction, 'M/D/YYYY');
    const timeZone = getTimeZoneLabel(info.dateInstruction.timeZone);
    const start = militaryHourToTime(info.timeInstruction.startTime);
    const end = militaryHourToTime(info.timeInstruction.endTime);
    return `${date} - between ${start} and ${end} ${timeZone}`;
  }).filter(nullishFilter);

  if (requested.length === 0) {
    requested.push('The patient will pick a date and time');
  }

  return requested;
};
const getProcedureSupplyItemIDs = (procedure, includeKitSupplyItem = true) => {
  var _a;

  const supplyItemIDs = [...Object.keys((_a = procedure.supplyItems) !== null && _a !== void 0 ? _a : {})];

  if (procedure.kitSupplyItem && includeKitSupplyItem) {
    supplyItemIDs.push(procedure.kitSupplyItem.id);
  }

  return supplyItemIDs.filter(nullishFilter);
};
const getGenericStatusInfoTitleByProcedureStatus = procedureStatus => {
  switch (procedureStatus) {
    case ProcedureStatus.COMPLETED:
      return GenericStatusInfoTitle.COMPLETED;

    case ProcedureStatus.CREATED:
      return GenericStatusInfoTitle.PENDING;

    case ProcedureStatus.IN_PROGRESS:
      return GenericStatusInfoTitle.IN_PROGRESS;

    case ProcedureStatus.FAILED:
      return GenericStatusInfoTitle.FAILED;

    case ProcedureStatus.CANCELED:
      return GenericStatusInfoTitle.CANCELED;

    case ProcedureStatus.DRAFT:
      return GenericStatusInfoTitle.DRAFT;
  }
};
const getGenericStatusIndicatorByProcedureStatus = procedureStatus => {
  switch (procedureStatus) {
    case ProcedureStatus.COMPLETED:
      return GenericStatusIndicator.GREEN;

    case ProcedureStatus.CREATED:
      return GenericStatusIndicator.WHITE;

    case ProcedureStatus.IN_PROGRESS:
      return GenericStatusIndicator.YELLOW;

    case ProcedureStatus.FAILED:
    case ProcedureStatus.CANCELED:
      return GenericStatusIndicator.RED;

    case ProcedureStatus.DRAFT:
      return GenericStatusIndicator.WHITE;
  }
};
const getProcedureStatusColor = procedureLike => {
  const {
    status
  } = procedureLike !== null && procedureLike !== void 0 ? procedureLike : {};
  if (!status) return GenericStatusIndicator.WHITE;
  return getGenericStatusIndicatorByProcedureStatus(status);
};
const determineProcedureFailureReason = mostRecentlyChangedSampleStatus => {
  switch (mostRecentlyChangedSampleStatus) {
    case SampleStatus.FAILED:
      return FailureReason.COLLECTION_FAILED;

    case SampleStatus.DROP_OFF_FAILED:
      return FailureReason.DROP_OFF_FAILED;

    default:
      throw Error('No failure reason found');
  }
};
const checkIfAllSamplesFailed = procedure => {
  var _a;

  const allSamples = Object.values((_a = procedure.samples) !== null && _a !== void 0 ? _a : {});
  return allSamples.every(sample => {
    var _a;

    return (sample.status === SampleStatus.FAILED || sample.status === SampleStatus.DROP_OFF_FAILED
    /**
     * CARE-4181: Product requested that sample with supplies that must be discarded after transfer are not included
     * in this logic
    */
    ) && !((_a = sample.processing) === null || _a === void 0 ? void 0 : _a[SupplyInstructionOptions.DISCARD_AFTER_TRANSFER]);
  });
};
const maybeGetProcedureFailedUpdates = async (procedure, reason, userID, userRole) => {
  var _a;

  if (hasStatus(procedure.status, ProcedureStatus.FAILED)) return {};
  const allSamplesFailed = Object.values((_a = procedure.samples) !== null && _a !== void 0 ? _a : {}).every(sample => hasStatus(sample.status, [SampleStatus.FAILED, SampleStatus.DROP_OFF_FAILED]));
  if (!allSamplesFailed) return {};
  const failed = {
    reason,
    failedBy: userID !== null && userID !== void 0 ? userID : 'unknown user',
    failedByRole: userRole,
    supplyCondition: SupplyCondition.SUPPLIES_USED,
    timestamp: Date.now()
  };
  return {
    [getProcedurePath(procedure.id, 'status')]: ProcedureStatus.FAILED,
    [getProcedurePath(procedure.id, 'failed')]: failed
  };
};
/**
 * Returns true if fasting instruction with `Yes` is present. Otherwise
 * returns false.
 */

const getFastingRequired = procedure => {
  const fastingInstruction = getInstruction(procedure.instructions, InstructionType.FASTING);
  return fastingInstruction.instruction === YesNoOptionLabel.YES;
};
/**
 * Rescheduling currently supported on pro (medical office staff) and patient.
 */

const getCancelationUserTypeForReschedule = role => {
  switch (role) {
    case Role.ASSISTANT:
      return CancelationUserType.ASSISTANT;

    case Role.PRESCRIBER:
      return CancelationUserType.PRESCRIBER;

    case Role.PATIENT:
      return CancelationUserType.PATIENT;

    case Role.NURSE:
      return CancelationUserType.NURSE;

    default:
      return CancelationUserType.CARESEND;
  }
};
/**
 * Determines if a procedure is ineligible for rescheduling.
 *
 * A procedure is considered ineligible if it was canceled or failed by the
 * demand side and is not medically active. The decision is based on the role of
 * the user attempting to cancel and the type of user they are canceling for.
 *
 * Logic:
 * - True if:
 *   - A prescriber or patient is canceling or being canceled for.
 * - False if:
 *   - A nurse is canceling or being canceled for.
 *   - An assistant canceling or being canceled for belongs to a staffing office.
 *
 * Notes:
 * - The `userRole` is always needed, and if missing, is extracted from the
 *   canceling `userType`.
 * - The `cancelingUserType` is preferred over `userRole`.
 * - The `officeType` is only defined for prescribers or assistants.
 */

const getDisallowRescheduling = (userRole, cancelingUserType, officeType) => {
  /** Not defined if a user other than a superadmin is canceling from the portal */
  const isCancelingUserDefined = cancelingUserType !== undefined;
  const isAssistant = isCancelingUserDefined ? cancelingUserType === CancelationUserType.ASSISTANT : userRole === Role.ASSISTANT;
  const isNurse = isCancelingUserDefined ? cancelingUserType === CancelationUserType.NURSE : userRole === Role.NURSE;
  const shouldBeReschedulable = isNurse || isAssistant && officeType === OfficeType.STAFFING;
  return !shouldBeReschedulable;
};
/**
 * If the contact is not custom, it is encrypted on draft waypoints, waypoints,
 * and procedures.
 */

const shouldEncryptProcedureContact = contact => !contact.custom;
/**
 * Given ICD-10 codes, returns the diagnosis instruction to be stored on the
 * procedure, and the diagnoses to be stored on the procedure and merged into
 * the patient's existing diagnoses.
 */

const getProcedureDiagnosesData = async icd10Codes => {
  /**
   * The diagnosis instruction is stored on the procedure separated by slashes.
   * Example: `A000 / I89.8 / V95.03XS`
   */
  const instruction = icd10Codes.map(({
    code
  }) => code).join(' / ');
  const diagnosisInstruction = {
    type: InstructionType.DIAGNOSIS,
    instruction
  };
  const diagnosesPromises = icd10Codes.map(async code => ({
    secretID: await decryptData(code.secretID),
    icd10Code: await encryptData(code)
  }));
  const diagnoses = await Promise.all(diagnosesPromises);
  return {
    diagnosisInstruction,
    diagnoses
  };
};

export { checkIfAllSamplesFailed, determineProcedureFailureReason, formatInstructions, getCancelationUserTypeForReschedule, getDisallowRescheduling, getFastingRequired, getFormattedInstructions, getGenericStatusIndicatorByProcedureStatus, getGenericStatusInfoTitleByProcedureStatus, getMinimumOrderedMedicalTitleByTaskID, getMinimumTitleByTask, getMinimumTitleForProcedures, getProcedureDateInfoDetailed, getProcedureDiagnosesData, getProcedureDocumentsByType, getProcedurePath, getProcedureSamplePath, getProcedureStatusColor, getProcedureSupplyItemIDs, getSupplyInstructionConfigOptionsPath, markSupplyInstructionConfigUpdateTime, maybeGetProcedureFailedUpdates, shouldEncryptProcedureContact, updateProcedureDocuments };
